import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, AlertTriangle, Clock, CheckCircle2, Shield } from 'lucide-react';

const C = { navy: '#0D1B2A', saffron: '#FF9933', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA', blue: '#1A4C7C' };

const URGENCY_CONFIG = {
  overdue: { color: C.red, label: 'Overdue', icon: AlertTriangle },
  urgent: { color: C.amber, label: '< 7 days', icon: Clock },
  upcoming: { color: C.teal, label: '< 30 days', icon: Calendar },
  future: { color: C.green, label: 'Future', icon: CheckCircle2 },
  unknown: { color: C.slate, label: 'TBD', icon: Calendar },
};

const TYPE_LABELS = {
  idr_deadline: 'IDR Deadline', apra_filing: 'APRA Filing', authority_expiry: 'Authority Expiry',
  training_due: 'Training Due', tmd_review: 'TMD Review', conflict_review: 'Conflict Review',
  asic_reporting: 'ASIC Report', treaty_expiry: 'Treaty Expiry',
};

export default function ComplianceCalendarPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/analytics/compliance-calendar').then(r => setData(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-sm" />)}</div>;
  if (!data) return null;

  const filtered = filter ? data.events.filter(e => e.urgency === filter) : data.events;

  return (
    <div className="space-y-5" data-testid="compliance-calendar-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: C.navy, fontFamily: "'IBM Plex Sans', sans-serif" }}>Compliance Calendar</h1>
        <p className="text-sm mt-1" style={{ color: C.slate }}>Unified regulatory deadline timeline — all 14 modules</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <button onClick={() => setFilter(filter === 'overdue' ? '' : 'overdue')} className={`text-left ${filter === 'overdue' ? 'ring-2 ring-red-300 rounded-sm' : ''}`}>
          <KPI label="Overdue" value={data.overdue} color={data.overdue > 0 ? C.red : C.green} />
        </button>
        <button onClick={() => setFilter(filter === 'urgent' ? '' : 'urgent')} className={`text-left ${filter === 'urgent' ? 'ring-2 ring-amber-300 rounded-sm' : ''}`}>
          <KPI label="Urgent (< 7d)" value={data.urgent} color={data.urgent > 0 ? C.amber : C.green} />
        </button>
        <button onClick={() => setFilter(filter === 'upcoming' ? '' : 'upcoming')} className={`text-left ${filter === 'upcoming' ? 'ring-2 ring-cyan-300 rounded-sm' : ''}`}>
          <KPI label="Upcoming (< 30d)" value={data.upcoming} color={C.teal} />
        </button>
        <KPI label="Total Events" value={data.total_events} color={C.blue} />
      </div>

      {/* Timeline */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)] flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Deadline Timeline</h3>
          {filter && <button onClick={() => setFilter('')} className="text-xs hover:underline" style={{ color: C.blue }}>Clear filter</button>}
        </div>
        <div className="divide-y divide-[rgba(136,150,168,0.08)]">
          {filtered.length === 0 ? (
            <p className="p-8 text-center text-sm" style={{ color: C.slate }}>No events match this filter</p>
          ) : filtered.map((event, i) => {
            const ucfg = URGENCY_CONFIG[event.urgency] || URGENCY_CONFIG.unknown;
            const UIcon = ucfg.icon;
            return (
              <div key={`${event.module}-${event.type}-${event.date}-${i}`} className="flex items-start gap-4 p-3 hover:bg-[#F5F7FA] transition-colors" data-testid={`event-${i}`}>
                {/* Urgency indicator */}
                <div className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0 mt-0.5" style={{ background: ucfg.color + '12' }}>
                  <UIcon className="w-4 h-4" style={{ color: ucfg.color }} />
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-semibold" style={{ color: C.navy }}>{event.title}</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ background: C.blue + '10', color: C.blue }}>{event.module}</span>
                  </div>
                  <p className="text-[11px]" style={{ color: C.slate }}>{event.detail}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: C.slate }}>{TYPE_LABELS[event.type] || event.type}</p>
                </div>
                {/* Date & urgency */}
                <div className="text-right shrink-0">
                  <p className="text-xs font-mono" style={{ color: ucfg.color }}>{event.date}</p>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ background: ucfg.color + '15', color: ucfg.color }}>{ucfg.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function KPI({ label, value, color }) {
  return (
    <Card className="p-3 rounded-sm border border-[rgba(136,150,168,0.15)] bg-white">
      <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p>
      <p className="text-base font-semibold mt-0.5" style={{ color: color || C.navy }}>{value}</p>
    </Card>
  );
}
